/* Keep the native product form/button in place; compact only its presentation after scrolling past it. */
(() => {
  'use strict';
  if (window.LoveCashmereCompactPurchase) return;
  const selector = 'product-info .buy-buttons[data-lc-compact-purchase]';
  const instances = new Map();
  const enterDistance = 24;
  const returnDistance = 4;

  function initPanel(panel) {
    if (instances.has(panel)) return;
    const form = panel.querySelector('product-form');
    const submit = form?.querySelector('button[type="submit"]');
    const toggle = panel.querySelector('[data-lc-purchase-toggle]');
    const selection = panel.querySelector('[data-lc-purchase-selection]');
    const productInfo = panel.closest('product-info');
    if (!form || !submit || !toggle || !panel.querySelector('.lc-purchase-price')) return;
    const sentinel = document.createElement('div');
    const spacer = document.createElement('div');
    sentinel.className = 'lc-compact-purchase__sentinel';
    spacer.className = 'lc-compact-purchase__spacer';
    sentinel.setAttribute('aria-hidden', 'true');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.setProperty('--lc-purchase-margin', getComputedStyle(panel).marginBottom);
    panel.before(sentinel);
    panel.after(spacer);
    panel.dataset.lcPurchaseReady = '';
    const state = { panel, productInfo, form, submit, toggle, sentinel, spacer, compact: false, past: false, manual: false, fullHeight: panel.getBoundingClientRect().height, frame: 0, measure: false };
    instances.set(panel, state);

    const hasExtraFields = () => !!form.querySelector('recipient-form, [name="selling_plan"], [name^="properties["]');
    const hasError = () => !!form.querySelector('.product-form__error-message-wrapper:not([hidden])');
    const hidesFocus = () => panel.contains(document.activeElement) && !submit.contains(document.activeElement) && !toggle.contains(document.activeElement);

    function syncSelection() {
      if (!selection) return;
      // Read the actual chosen controls, including combinations without an available variant.
      // This also survives Shopify replacing variant-selects after its section response.
      const picker = productInfo.querySelector('variant-selects');
      const chosen = picker ? [...picker.querySelectorAll('input[type="radio"]:checked, select option:checked')] : [];
      selection.querySelectorAll('[data-lc-purchase-option]').forEach((row) => {
        const name = row.dataset.lcPurchaseOption.trim().toLowerCase();
        const control = chosen.find((input) => input.dataset.optionName?.trim().toLowerCase() === name);
        if (control && row.querySelector('dd').textContent !== control.value) row.querySelector('dd').textContent = control.value;
      });
      selection.hidden = !state.compact || !selection.children.length;
    }

    function reserve() {
      const height = state.compact ? Math.max(0, state.fullHeight - panel.getBoundingClientRect().height) : 0;
      spacer.style.setProperty('--lc-purchase-reserve', `${height}px`);
    }

    function render(compact) {
      state.compact = compact;
      panel.toggleAttribute('data-lc-purchase-compact', compact);
      panel.toggleAttribute('data-lc-purchase-expanded', state.manual && state.past && !compact);
      toggle.hidden = !compact && !state.manual;
      toggle.setAttribute('aria-expanded', String(!compact));
      toggle.setAttribute('aria-label', compact ? 'Show purchase details and payment options' : 'Close purchase details and payment options');
      toggle.querySelector('[data-lc-purchase-toggle-label]').textContent = compact ? 'Details' : 'Close';
      syncSelection();
      reserve();
    }

    function measureFull() {
      // A synchronous style measurement: no cloned inputs/forms, no intermediate paint or focus move.
      const compact = state.compact;
      const expanded = panel.hasAttribute('data-lc-purchase-expanded');
      panel.removeAttribute('data-lc-purchase-compact');
      panel.removeAttribute('data-lc-purchase-expanded');
      const selectionHidden = selection?.hidden;
      if (selection) selection.hidden = true;
      // Keep a focused disclosure in the accessibility tree while excluding its box from flow.
      const position = toggle.style.position;
      toggle.style.position = 'absolute';
      state.fullHeight = panel.getBoundingClientRect().height;
      toggle.style.position = position;
      panel.toggleAttribute('data-lc-purchase-compact', compact);
      panel.toggleAttribute('data-lc-purchase-expanded', expanded);
      if (selection) selection.hidden = selectionHidden;
    }

    function update() {
      state.frame = 0;
      if (!panel.isConnected) return destroy(panel);
      const style = getComputedStyle(panel);
      const top = parseFloat(style.top);
      const available = style.position === 'sticky' && Number.isFinite(top) && !hasExtraFields();
      const distance = available ? top - sentinel.getBoundingClientRect().top : -Infinity;
      if (!available || distance <= returnDistance) {
        state.past = false;
        state.manual = false;
      } else if (distance > enterDistance) state.past = true;
      if (state.measure || !state.compact) {
        state.measure = false;
        measureFull();
      }
      let compact = state.past && !state.manual && !hasError() && !hidesFocus();
      // Posting keeps the current geometry, including the original spinner and button focus.
      if (submit.classList.contains('loading') && !hasError()) compact = state.compact;
      render(compact);
    }

    function schedule(measure = false) {
      state.measure ||= measure;
      if (!state.frame) state.frame = requestAnimationFrame(update);
    }
    state.schedule = schedule;
    state.onToggle = () => {
      state.manual = state.compact;
      schedule(true);
    };
    state.onFocus = () => schedule();
    toggle.addEventListener('click', state.onToggle);
    panel.addEventListener('focusin', state.onFocus);
    panel.addEventListener('focusout', state.onFocus);
    state.observer = new MutationObserver((records) => {
      // Ignore our panel/toggle state writes. Native price/form updates need a fresh full-height reserve.
      if (records.some((record) => !toggle.contains(record.target) && !selection?.contains(record.target) && record.target !== toggle && record.target !== panel)) schedule(true);
    });
    state.observer.observe(panel, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'disabled', 'aria-disabled'] });
    state.onSelection = (event) => { if (event.target.closest('variant-selects')) schedule(true); };
    productInfo.addEventListener('change', state.onSelection);
    state.optionObserver = new MutationObserver((records) => {
      if (records.some((record) => record.target.closest?.('variant-selects') || [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === Node.ELEMENT_NODE && (node.matches('variant-selects') || node.querySelector('variant-selects'))))) schedule(true);
    });
    state.optionObserver.observe(productInfo, { childList: true, subtree: true });
    if ('ResizeObserver' in window) {
      let width = panel.getBoundingClientRect().width;
      let height = panel.getBoundingClientRect().height;
      state.resize = new ResizeObserver(() => {
        const current = panel.getBoundingClientRect();
        if (Math.abs(current.width - width) > 0.5 || (!state.compact && Math.abs(current.height - height) > 0.5)) schedule(true);
        width = current.width;
        height = current.height;
      });
      state.resize.observe(panel);
    }
    schedule(true);
  }

  function destroy(panel) {
    const state = instances.get(panel);
    if (!state) return;
    cancelAnimationFrame(state.frame);
    state.observer.disconnect();
    state.optionObserver.disconnect();
    state.productInfo.removeEventListener('change', state.onSelection);
    state.resize?.disconnect();
    state.toggle.removeEventListener('click', state.onToggle);
    panel.removeEventListener('focusin', state.onFocus);
    panel.removeEventListener('focusout', state.onFocus);
    state.sentinel.remove();
    state.spacer.remove();
    panel.removeAttribute('data-lc-purchase-ready');
    panel.removeAttribute('data-lc-purchase-compact');
    panel.removeAttribute('data-lc-purchase-expanded');
    state.toggle.hidden = true;
    const selection = panel.querySelector('[data-lc-purchase-selection]');
    if (selection) selection.hidden = true;
    instances.delete(panel);
  }

  function init(scope = document) {
    if (scope.matches?.(selector)) initPanel(scope);
    scope.querySelectorAll?.(selector).forEach(initPanel);
  }
  window.addEventListener('scroll', () => instances.forEach((state) => state.schedule()), { passive: true });
  window.addEventListener('resize', () => instances.forEach((state) => state.schedule(true)), { passive: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((state, panel) => { if (event.target.contains(panel)) destroy(panel); });
  });
  function boot() {
    init();
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) init(node);
      }));
      if (records.some((record) => record.removedNodes.length)) instances.forEach((state, panel) => { if (!panel.isConnected) destroy(panel); });
    }).observe(document.body, { childList: true, subtree: true });
  }
  window.LoveCashmereCompactPurchase = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
